// Session 54 · Feedback modal.
//
// Accessible from the header user menu. Signed-in users get their identity
// stamped automatically; anonymous users can add name + email inline. Every
// submission fires an admin notification via POST /api/feedback.
import { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function FeedbackModal({ open, onClose }) {
  const { user } = useAuth();
  const [kind, setKind] = useState("feedback");
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim() || message.trim().length < 3) {
      toast.error("Please share a few more words"); return;
    }
    if (!user && (!email.trim() || !name.trim())) {
      toast.error("Add your name and email so we can follow up"); return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        kind, message,
        page_url: typeof window !== "undefined" ? window.location.href : null,
        name: user ? null : name,
        email: user ? null : email,
      });
      toast.success("Thanks! Your feedback landed with the team.");
      setMessage(""); setKind("feedback");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — try again?");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" data-testid="feedback-modal"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        style={{
          position: "fixed", inset: 0, zIndex: 100,
          background: "rgba(15,23,41,0.72)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}>
      <form onSubmit={submit} style={{
        background: "var(--navy-mid, #1a2340)", color: "#fff",
        border: "1px solid rgba(212,168,75,0.28)", borderRadius: 14,
        padding: 28, maxWidth: 480, width: "100%",
        boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase",
                            color: "var(--gold, #B8913A)", marginBottom: 6, fontWeight: 700 }}>
              Share feedback
            </div>
            <h2 style={{ fontSize: 20, margin: 0, fontWeight: 700 }}>Help us build Métier</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            data-testid="feedback-close"
            style={{ background: "none", border: "none", color: "rgba(255,255,255,0.65)",
                       fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 }}>
          {[
            { key: "feedback", label: "Feedback" },
            { key: "bug", label: "Bug" },
            { key: "feature", label: "Feature idea" },
          ].map(o => (
            <button key={o.key} type="button" onClick={() => setKind(o.key)}
              data-testid={`feedback-kind-${o.key}`}
              style={{
                padding: "10px 12px", borderRadius: 8, cursor: "pointer",
                background: kind === o.key ? "rgba(184,145,58,0.18)" : "transparent",
                border: `1px solid ${kind === o.key ? "#B8913A" : "rgba(255,255,255,0.12)"}`,
                color: kind === o.key ? "#B8913A" : "rgba(255,255,255,0.85)",
                fontSize: 12, fontWeight: 600, letterSpacing: "0.04em",
              }}>{o.label}</button>
          ))}
        </div>

        {!user && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
            <input placeholder="Your name" value={name} onChange={e => setName(e.target.value)}
              data-testid="feedback-name" required
              style={inputStyle}/>
            <input placeholder="Email" type="email" value={email} onChange={e => setEmail(e.target.value)}
              data-testid="feedback-email" required
              style={inputStyle}/>
          </div>
        )}

        <textarea value={message} onChange={e => setMessage(e.target.value)}
          placeholder={kind === "bug" ? "Steps to reproduce, expected vs actual…" :
                          kind === "feature" ? "What would make Métier better for you?" :
                            "Share anything on your mind…"}
          data-testid="feedback-message"
          rows={5} required
          style={{ ...inputStyle, resize: "vertical", minHeight: 110 }}/>

        <div style={{ marginTop: 8, fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
          Sent from {typeof window !== "undefined" ? window.location.pathname : "—"}
          {user ? ` · as ${user.name}` : ""}
        </div>

        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button type="button" onClick={onClose} data-testid="feedback-cancel"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.16)",
                       color: "rgba(255,255,255,0.85)", padding: "10px 20px",
                       borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Cancel
          </button>
          <button type="submit" disabled={submitting} data-testid="feedback-submit"
            style={{ background: "#B8913A", border: "1px solid #B8913A",
                       color: "#fff", padding: "10px 22px",
                       borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer",
                       opacity: submitting ? 0.55 : 1 }}>
            {submitting ? "Sending…" : "Send"}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputStyle = {
  width: "100%", background: "var(--navy-dark, #0f1729)",
  border: "1px solid rgba(212,168,75,0.22)", borderRadius: 8,
  color: "#fff", padding: "10px 12px", fontSize: 14, fontFamily: "inherit",
};
